/**
 * FormField Component
 *
 * A wrapper for form inputs that provides consistent labeling, error messages, and help text.
 */

import React from 'react';
import { cn } from '@lognai/synapse-design-system-v2';

export interface FormFieldProps {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  error?: string;
  helpText?: string;
  htmlFor?: string;
  className?: string;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  children,
  required = false,
  error,
  helpText,
  htmlFor,
  className,
}) => {
  return (
    <div className={cn('flex flex-col gap-2 mb-4', className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-foreground flex gap-1">
        {label}
        {required && <span className="text-destructive">*</span>}
      </label>
      {children}
      {/* `data-field-error` is the hook a long form uses to scroll the first
          blocking field back into view when a submit is rejected — see
          ContractTermForm (QA ledger DND-004). Marking it here rather than at
          each call site means any form gets it for free. */}
      {error && <p data-field-error className="text-sm text-destructive m-0">{error}</p>}
      {helpText && !error && <p className="text-sm text-muted-foreground m-0">{helpText}</p>}
    </div>
  );
};

export default FormField;
